import { useMemo, useState } from "react";
import { Alert, Button, Card, Input, Modal, PasswordInput, Switch, TextArea } from "../../ds";
import type { DeviceDetail, SystemSettings } from "../../lib/api";
import { SectionCard } from "../SectionCard";
import { useDeviceAction } from "../../lib/hooks/useDeviceAction";
import { useSection } from "../../lib/hooks/useSection";

// Sektion „System“ → Einstellungen (action `system_get` / `system_set`).
//
// Geschickt wird nur, was der Bediener wirklich geändert hat: der Agent lässt
// alles Ungenannte stehen, und die Bestätigung zeigt genau diese Liste. Das
// Root-Passwort läuft über eine eigene Aktion (docs/wire-protocol-v1.md).
//
// Interfaces, LAN-Adressen und DHCP-Pools stehen in der Sektion „Netzwerk“ —
// die hängen am Interface, nicht am Router.

interface Form {
  hostname: string;
  zonename: string;
  timezone: string;
  /** Zeitquellen in chrony.conf-Schreibweise: `pool <host>` / `server <host>`,
   *  eine je Zeile. Bewusst dieselbe Syntax wie chrony selbst — wer NTP pflegt,
   *  kennt sie, und eine eigene Schreibweise zu erfinden wäre eine zweite. */
  ntpSources: string;
  sshPort: string;
  sshPasswordAuth: boolean;
  sshRootPasswordAuth: boolean;
  sshKeys: string;
}

/** Formularfeld → Wire-Param + Art. Die eine Wahrheit über die Abbildung. */
const FIELDS: { key: keyof Form; param: string; kind: "text" | "bool" | "num" | "list" }[] = [
  { key: "hostname", param: "hostname", kind: "text" },
  { key: "zonename", param: "zonename", kind: "text" },
  { key: "timezone", param: "timezone", kind: "text" },
  { key: "sshPort", param: "ssh_port", kind: "num" },
  { key: "sshPasswordAuth", param: "ssh_password_auth", kind: "bool" },
  { key: "sshRootPasswordAuth", param: "ssh_root_password_auth", kind: "bool" },
  { key: "sshKeys", param: "ssh_keys", kind: "list" },
];

const LABELS: Record<string, string> = {
  hostname: "Hostname",
  zonename: "Zeitzone",
  timezone: "Zeitzone (POSIX)",
  ntp_sources: "Zeitserver",
  ssh_port: "SSH-Port",
  ssh_password_auth: "SSH-Passwortanmeldung",
  ssh_root_password_auth: "Root darf sich mit Passwort anmelden",
  ssh_keys: "Autorisierte SSH-Schlüssel",
};

function toForm(s: SystemSettings | null): Form {
  return {
    hostname: s?.hostname ?? "",
    zonename: s?.zonename ?? "",
    timezone: s?.timezone ?? "",
    ntpSources: (s?.ntp?.sources ?? [])
      .map((src) => `${src.pool ? "pool" : "server"} ${src.hostname}`)
      .join("\n"),
    sshPort: s?.ssh?.port !== undefined ? String(s.ssh.port) : "",
    sshPasswordAuth: s?.ssh?.passwordAuth ?? true,
    sshRootPasswordAuth: s?.ssh?.rootPasswordAuth ?? true,
    sshKeys: (s?.ssh?.keys ?? []).join("\n"),
  };
}

/** Nur die geänderten Felder als flache Wire-Params — alles andere bleibt auf
 *  dem Router stehen. Ein leergeräumtes Textfeld wird nicht geschickt: „nichts
 *  eintragen“ heißt hier „nicht anfassen“, nicht „löschen“. */
/** Zeilen wie `pool host` / `server host` / `host` → Wire-Zeilen.
 *
 *  Die `id` wird über (hostname, pool) aus dem Ist-Stand zurückgeholt: eine
 *  unveränderte Quelle behält damit ihre uci-Section — und mit ihr die
 *  Optionen, die diese Oberfläche gar nicht kennt (`iburst`, `maxpoll`, `nts`).
 *  Ohne das Wiederfinden würde jedes Speichern sie stillschweigend wegwerfen. */
function ntpSourceRows(text: string, current: SystemSettings | null): Record<string, unknown>[] {
  const known = current?.ntp?.sources ?? [];
  return text
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const [head, ...rest] = line.split(/\s+/);
      const pool = head === "pool";
      const hostname = rest.length > 0 ? rest.join(" ") : head;
      const match = known.find((k) => k.hostname === hostname && k.pool === pool);
      return match ? { id: match.id, hostname, pool } : { hostname, pool };
    });
}

function changedParams(
  form: Form,
  base: Form,
  current: SystemSettings | null,
): Record<string, unknown> {
  const params: Record<string, unknown> = {};
  if (form.ntpSources !== base.ntpSources) {
    params.ntp_sources = ntpSourceRows(form.ntpSources, current);
  }
  for (const field of FIELDS) {
    const value = form[field.key];
    if (value === base[field.key]) continue;
    if (field.kind === "bool") {
      params[field.param] = value;
    } else if (field.kind === "list") {
      params[field.param] = String(value)
        .split("\n")
        .map((line) => line.trim())
        .filter(Boolean);
    } else {
      const text = String(value).trim();
      if (text) params[field.param] = field.kind === "num" ? Number(text) : text;
    }
  }
  return params;
}

function IdentityCard({
  form,
  locked,
  set,
}: {
  form: Form;
  locked: boolean;
  set: <K extends keyof Form>(key: K, value: Form[K]) => void;
}) {
  return (
    <Card title="Identität und Zeit">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Input
          label="Hostname"
          value={form.hostname}
          disabled={locked}
          onChange={(e) => set("hostname", e.target.value)}
        />
        <Input
          label="Zeitzone"
          helperText="tz-Name, z.B. Europe/Berlin"
          value={form.zonename}
          disabled={locked}
          onChange={(e) => set("zonename", e.target.value)}
        />
        <Input
          label="Zeitzone (POSIX)"
          helperText="Wie in uci, z.B. CET-1CEST,M3.5.0,M10.5.0/3"
          value={form.timezone}
          disabled={locked}
          onChange={(e) => set("timezone", e.target.value)}
        />
      </div>
    </Card>
  );
}

function NtpCard({
  form,
  locked,
  set,
}: {
  form: Form;
  locked: boolean;
  set: <K extends keyof Form>(key: K, value: Form[K]) => void;
}) {
  return (
    <Card title="Zeitserver (NTP)">
      <div className="flex flex-col gap-3">
        <TextArea
          label="Zeitquellen"
          helperText="Eine je Zeile, wie in chrony.conf: „pool 2.openwrt.pool.ntp.org“ oder „server ptbtime1.ptb.de“. Ohne Präfix gilt server."
          rows={4}
          value={form.ntpSources}
          disabled={locked}
          onChange={(e) => set("ntpSources", e.target.value)}
        />
        <p className="text-xs text-content-3">
          Ein Pool löst auf mehrere Adressen auf und chrony hält mehrere davon gleichzeitig — für
          einen einzelnen Zeitserver ist „server“ richtig.
        </p>
      </div>
    </Card>
  );
}

function SshCard({
  form,
  locked,
  set,
}: {
  form: Form;
  locked: boolean;
  set: <K extends keyof Form>(key: K, value: Form[K]) => void;
}) {
  return (
    <Card title="SSH (Dropbear)">
      <div className="flex flex-col gap-3">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Input
            label="Port"
            inputMode="numeric"
            value={form.sshPort}
            disabled={locked}
            onChange={(e) => set("sshPort", e.target.value)}
          />
          <Switch
            label="Passwortanmeldung"
            checked={form.sshPasswordAuth}
            disabled={locked}
            onChange={(v) => set("sshPasswordAuth", v)}
          />
          <Switch
            label="Root mit Passwort"
            checked={form.sshRootPasswordAuth}
            disabled={locked}
            onChange={(v) => set("sshRootPasswordAuth", v)}
          />
        </div>
        <TextArea
          label="Autorisierte Schlüssel"
          helperText="Einer je Zeile, Format wie in authorized_keys. Leeren entfernt alle."
          rows={5}
          className="font-mono text-xs"
          value={form.sshKeys}
          disabled={locked}
          onChange={(e) => set("sshKeys", e.target.value)}
        />
      </div>
    </Card>
  );
}

// Nur eingehängt, solange offen (`{passwordOpen && <RootPasswordModal …>}`) —
// so trägt kein getipptes Passwort in die nächste Öffnung, ohne dass es dafür
// einen Effekt braucht, der bei jedem Schließen Zustand zurücksetzt.
function RootPasswordModal({
  busy,
  onClose,
  onSubmit,
}: {
  busy: boolean;
  onClose: () => void;
  onSubmit: (password: string) => void;
}) {
  const [password, setPassword] = useState("");
  const [repeat, setRepeat] = useState("");
  const tooShort = password.length > 0 && password.length < 8;
  const mismatch = repeat.length > 0 && password !== repeat;

  return (
    <Modal
      open
      onClose={onClose}
      title="Root-Passwort setzen"
      subtitle="Gilt für SSH und LuCI auf dem Router."
      size="sm"
      actions={
        <>
          <Button variant="ghost" onClick={onClose}>
            Abbrechen
          </Button>
          <Button
            variant="primary"
            iconName="key"
            loading={busy}
            disabled={password.length < 8 || password !== repeat}
            onClick={() => onSubmit(password)}
          >
            Setzen
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <Alert variant="neutral">
          Der Router setzt das Passwort über seinen eigenen rpcd — es steht danach weder in der
          Audit-Historie noch im Log, dort erscheint nur „***“.
        </Alert>
        <PasswordInput
          label="Neues Passwort"
          autoComplete="new-password"
          value={password}
          error={tooShort ? "Mindestens 8 Zeichen." : undefined}
          onChange={(e) => setPassword(e.target.value)}
        />
        <PasswordInput
          label="Wiederholen"
          autoComplete="new-password"
          value={repeat}
          error={mismatch ? "Die beiden Eingaben sind verschieden." : undefined}
          onChange={(e) => setRepeat(e.target.value)}
        />
      </div>
    </Modal>
  );
}

function ConfirmModal({
  params,
  busy,
  onClose,
  onConfirm,
}: {
  params: Record<string, unknown> | null;
  busy: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) {
  return (
    <Modal
      open={params !== null}
      onClose={onClose}
      title="Systemeinstellungen ändern?"
      subtitle="Nur diese Felder gehen zum Router — alles andere bleibt stehen."
      actions={
        <>
          <Button variant="ghost" onClick={onClose}>
            Abbrechen
          </Button>
          <Button variant="primary" iconName="check" loading={busy} onClick={onConfirm}>
            Übernehmen
          </Button>
        </>
      }
    >
      <dl className="grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">
        {Object.entries(params ?? {}).map(([key, value]) => (
          <div key={key} className="flex flex-col">
            <dt className="text-xs uppercase tracking-wide text-content-3">{LABELS[key] ?? key}</dt>
            <dd className="text-content-1 font-mono break-words">
              {Array.isArray(value)
                ? value.join(", ") || "— (leer)"
                : typeof value === "boolean"
                  ? value
                    ? "an"
                    : "aus"
                  : String(value)}
            </dd>
          </div>
        ))}
      </dl>
    </Modal>
  );
}

export function SystemSettingsCard({ device }: { device: DeviceDetail }) {
  const section = useSection<SystemSettings>(device, "system");
  const { busy, note, setNote, run } = useDeviceAction(device.id);
  const [pending, setPending] = useState<Record<string, unknown> | null>(null);
  const [passwordOpen, setPasswordOpen] = useState(false);

  // Frischer Stand vom Router → Formular neu aufsetzen. Das verwirft lokale
  // Eingaben, ist aber das ehrlichere Verhalten: angezeigt wird, was auf der
  // Box steht.
  //
  // Die Eingaben merken sich, zu welchem Stand sie getippt wurden. Kommt ein
  // neuer, passen sie nicht mehr und das Formular zeigt wieder den Router —
  // dasselbe Ergebnis wie ein Effekt, aber ohne den Renderlauf dazwischen, in
  // dem noch die alten Werte stehen.
  const base = useMemo(() => toForm(section.data), [section.data]);
  const [edited, setEdited] = useState<{ of: SystemSettings | null; form: Form } | null>(null);
  const form = edited?.of === section.data ? edited.form : base;

  const set = <K extends keyof Form>(key: K, value: Form[K]) =>
    setEdited({ of: section.data, form: { ...form, [key]: value } });

  const changed = changedParams(form, base, section.data);
  const dirty = Object.keys(changed).length > 0;
  const locked = !device.online || busy !== null;

  async function apply() {
    const params = pending;
    setPending(null);
    if (!params) return;
    const result = await run(
      "system_set",
      params,
      "Übernommen — der Router hat die betroffenen Dienste neu geladen.",
    );
    if (result) await section.refresh();
  }

  return (
    <>
      <SectionCard
        title="Einstellungen"
        description="Hostname, Zeit, NTP, SSH und Root-Passwort"
        section={section}
        deviceOnline={device.online}
        iconName="gear"
      >
        {() => (
          <div className="flex flex-col gap-5">
            {note && (
              <Alert variant={note.variant} dismissible onDismiss={() => setNote(null)}>
                {note.text}
              </Alert>
            )}

            <IdentityCard form={form} locked={locked} set={set} />
            <NtpCard form={form} locked={locked} set={set} />
            <SshCard form={form} locked={locked} set={set} />

            <Card title="Root-Passwort">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="text-sm text-content-2">
                  Der Router gibt sein Passwort nicht heraus — auch nicht, ob eines gesetzt ist.
                  Hier lässt es sich nur überschreiben.
                </p>
                <Button
                  variant="outline"
                  iconName="key"
                  disabled={locked}
                  onClick={() => setPasswordOpen(true)}
                >
                  Passwort setzen
                </Button>
              </div>
            </Card>

            <div className="flex flex-wrap items-center justify-end gap-3">
              <span className="mr-auto text-sm text-content-3">
                {dirty
                  ? `${Object.keys(changed).length} Feld(er) geändert — noch nicht auf dem Router.`
                  : "Keine Änderungen."}
              </span>
              <Button variant="ghost" disabled={!dirty || locked} onClick={() => setEdited(null)}>
                Verwerfen
              </Button>
              <Button
                variant="primary"
                iconName="check"
                loading={busy === "system_set"}
                disabled={!dirty || locked}
                onClick={() => setPending(changed)}
              >
                Übernehmen
              </Button>
            </div>
          </div>
        )}
      </SectionCard>

      <ConfirmModal
        params={pending}
        busy={busy === "system_set"}
        onClose={() => setPending(null)}
        onConfirm={() => void apply()}
      />
      {passwordOpen && (
        <RootPasswordModal
          busy={busy === "root_password_set"}
          onClose={() => setPasswordOpen(false)}
          onSubmit={(password) => {
            setPasswordOpen(false);
            void run("root_password_set", { password }, "Root-Passwort gesetzt.");
          }}
        />
      )}
    </>
  );
}
